import type {
  ApplicationValidationError,
  ConfigurationValidationError,
  DependencyValidationError,
  TemplateApplicationValidationError,
  TemplateDependencyExpressionValidationError,
  TemplateDependencyFunctionValidationError,
  TemplateDependencyValidationError,
} from '@festina/configuration-general-types/configuration'
import type { ExcludeString, Optional } from '@festina-life/core'

import { LayerKey } from '../layers/LayerKey'
import type { Tree } from '../types'
import type { ErrorLocation, LocationErrors } from './types'

/**
 * Temporary function to generate global keys for errors such that duplicated errors can be filtered out.
 * While we have 'proto-layers' we need to send out validation requests for all layers in the configuration,
 * which will result in duplicated errors. This function generates a global key for each error such that duplicated errors can be filtered out.
 */
export const getValidationErrorKey = (
  error: ConfigurationValidationError,
): string => {
  const typeWithoutTsString = error._type as ExcludeString<typeof error._type>

  switch (typeWithoutTsString) {
    case 'ChangesetValidationError':
    case 'ChangesetApplicationValidationError':
    case 'ChangesetDependencyValidationError':
      return `${typeWithoutTsString}:${error.changesetKey}:${error.errorMessage}`
    case 'ApplicationValidationError':
    case 'DependencyValidationError': {
      const { operationKey, errorMessage } = error as
        | ApplicationValidationError
        | DependencyValidationError
      return `${typeWithoutTsString}:${operationKey}:${errorMessage}`
    }
    case 'TemplateApplicationValidationError':
    case 'TemplateDependencyValidationError':
    case 'TemplateDependencyExpressionValidationError': {
      const { operationKey, errorMessage, path } = error as
        | TemplateApplicationValidationError
        | TemplateDependencyValidationError
        | TemplateDependencyExpressionValidationError
      return `${typeWithoutTsString}:${operationKey}:${path}:${errorMessage}`
    }
    case 'TemplateDependencyFunctionValidationError': {
      const { operationKey, errorMessage, path, isDynamic, source } =
        error as TemplateDependencyFunctionValidationError
      return `${typeWithoutTsString}:${operationKey}:${path}:${isDynamic}:${source}:${errorMessage}`
    }
  }
}

/**
 *
 * @param layerName Given a topology Tree and a layer name, return the topological path sorted from the most general to the most specific.
 * @param topology
 */
export const resolveTopologicalPath = (
  layer: LayerKey,
  topology: Optional<Tree>,
): LayerKey[] => {
  if (!layer.layerId) {
    return noLayerPathForTenant(layer.tenantId)
  }

  if (!topology) {
    throw new Error('No topology configured for the current branch.')
  }

  if (layer.layerId === topology.name) {
    return layer.tenantId
      ? [new LayerKey({ layerId: layer.layerId, tenantId: null }), layer]
      : [layer]
  }

  const childPaths = topology.children
    .map((childTopology) => resolveTopologicalPath(layer, childTopology))
    .find((path) => path.length > 0)

  return childPaths
    ? [new LayerKey({ layerId: topology.name, tenantId: null }), ...childPaths]
    : []
}

export const resolveTopologicalPathForBlock = (
  block: string,
  topology: Optional<Tree>,
): string[] => {
  if (!topology) {
    throw new Error('No topology configured for the current branch.')
  }

  if (block === topology.name) {
    return [block]
  }

  const childPaths = topology.children
    .map((childTopology) =>
      resolveTopologicalPathForBlock(block, childTopology),
    )
    .find((path) => path.length > 0)

  return childPaths ? [topology.name, ...childPaths] : []
}

export const filterDuplicatedErrorsFromGeneralToSpecificLayers = ({
  errors: allErrors,
  layerTopology,
  blockTopology,
}: {
  errors: LocationErrors[]
  layerTopology: Optional<Tree>
  blockTopology: Optional<Tree>
}): LocationErrors[] => {
  const mostGeneralErrorMap = new Map<string, ErrorLocation>()

  allErrors.forEach(({ layer, block, errors: locationErrors }) => {
    locationErrors.forEach((error) => {
      const errorKey = getValidationErrorKey(error)

      const existingErrorLocation = mostGeneralErrorMap.get(errorKey)
      if (existingErrorLocation) {
        const blockTopologicalPath = resolveTopologicalPathForBlock(
          existingErrorLocation.block,
          blockTopology,
        )
        const layerTopologicalPath = resolveTopologicalPath(
          existingErrorLocation.layer,
          layerTopology,
        )

        const generalLayersForLayerKey = layerTopologicalPath.slice(
          0,
          layerTopologicalPath.length - 1,
        )
        const generalBlocksForBlock = blockTopologicalPath.slice(
          0,
          blockTopologicalPath.length - 1,
        )

        const isCurrentLocationErrorMoreGeneral =
          block !== existingErrorLocation.block
            ? generalBlocksForBlock.includes(block)
            : generalLayersForLayerKey.some((generalLayer) =>
                generalLayer.equals(layer),
              )

        if (isCurrentLocationErrorMoreGeneral) {
          mostGeneralErrorMap.set(errorKey, { layer, block })
        }
      } else {
        mostGeneralErrorMap.set(errorKey, { layer, block })
      }
    })
  })

  return allErrors.map(({ layer, block, errors, serverError }) => ({
    layer,
    block,
    serverError,
    errors: errors.filter((error) => {
      const errorKey = getValidationErrorKey(error)
      const mostGeneralErrorLocation = mostGeneralErrorMap.get(errorKey)

      return (
        mostGeneralErrorLocation?.layer.equals(layer) &&
        mostGeneralErrorLocation?.block === block
      )
    }),
  }))
}

const noLayerPathForTenant = (tenantId: Optional<string>): LayerKey[] =>
  tenantId
    ? [
        new LayerKey({ layerId: null, tenantId: null }),
        new LayerKey({ layerId: null, tenantId }),
      ]
    : [new LayerKey({ layerId: null, tenantId: null })]

export const fetchAndDeduplicateErrors = async ({
  blocks,
  layers,
  fetcher,
  layerTopology,
  blockTopology,
}: {
  blocks: string[]
  layers: LayerKey[]
  fetcher: (props: {
    layer: LayerKey
    block: string
  }) => Promise<ConfigurationValidationError[] | null>
  layerTopology: Optional<Tree>
  blockTopology: Optional<Tree>
}) => {
  const layerAndBlockPairs = blocks.flatMap((block) =>
    layers.map((layer) => ({ layer, block })),
  )

  const allErrors = await Promise.allSettled(
    layerAndBlockPairs.map(({ layer, block }) => fetcher({ layer, block })),
  )

  const locatedErrors = allErrors.map((result, index) => {
    const { layer, block } = layerAndBlockPairs[index]
    return {
      layer,
      block,
      errors: result.status === 'fulfilled' ? (result.value ?? []) : [],
      serverError:
        result.status === 'rejected'
          ? (result.reason.body ?? new Error(result.reason.message))
          : undefined,
    }
  }) satisfies LocationErrors[]

  return filterDuplicatedErrorsFromGeneralToSpecificLayers({
    errors: locatedErrors,
    layerTopology,
    blockTopology,
  })
}
